import { useCallback, useEffect, useMemo, useRef, useState } from 'react'

import { getTemplateAttributeMoveUpdate } from '@/pages/DocumentPage/components/templateAttributeGeometry'
import { useDocumentState } from '@/state'
import {
  Position,
  TemplateAttribute,
  useUpdateTemplateElement,
} from '@/services/hooks/template_attributes'

type KeyboardNudgeDocumentPage = {
  id?: string | null
  template_values?: Record<string, unknown> | null
}

type KeyboardNudgeEvent = Pick<
  KeyboardEvent,
  'code' | 'key' | 'preventDefault' | 'repeat' | 'shiftKey' | 'stopPropagation'
> & {
  target?: EventTarget | null
}

const ZERO_POSITION: Position = { x: 0, y: 0 }

const isZeroPosition = (position: Position) =>
  position.x === 0 && position.y === 0

export type UseKeyboardNudgeOptions = {
  bindToWindow?: boolean
  isActive: boolean
  step?: number
  fineStep?: number
  repeatDelayMs?: number
  repeatIntervalMs?: number
  onPreviewChange?: (previewDelta: Position) => void
  onCommit?: (delta: Position) => void
  onNudgeStart?: (showGrid: boolean) => void
  onNudgeEnd?: (hideGrid: boolean) => void
  showGridOnNudge?: boolean
  preventDefault?: boolean
  documentPage: KeyboardNudgeDocumentPage
  template?: TemplateAttribute<any> | null
  commitDebounceMs?: number
}

export type UseKeyboardNudgeReturn = {
  bind: {
    onKeyDown?: (e: React.KeyboardEvent) => void
    onKeyUp?: (e: React.KeyboardEvent) => void
  }
  previewDelta: Position
  resetPreview: () => void
}

export const useKeyboardNudge = ({
  bindToWindow = false,
  isActive,
  step = 10,
  fineStep = 1,
  repeatDelayMs = 250,
  repeatIntervalMs = 50,
  onPreviewChange,
  onCommit,
  onNudgeEnd,
  onNudgeStart,
  showGridOnNudge = false,
  preventDefault = true,
  commitDebounceMs = 120,
  documentPage,
  template,
}: UseKeyboardNudgeOptions): UseKeyboardNudgeReturn => {
  const [preview, setPreview] = useState<Position>(ZERO_POSITION)
  const zoom = useDocumentState((state) => state.zoom)
  const zoomPercentage = useMemo(() => zoom / 100, [zoom])

  const heldRef = useRef<Set<string>>(new Set())
  const shiftRef = useRef(false)
  const delayRef = useRef<number | null>(null)
  const intervalRef = useRef<number | null>(null)
  const pendingDeltaRef = useRef<Position>({ x: 0, y: 0 })
  const commitTimeoutRef = useRef<number | null>(null)
  const inFlightCommitRef = useRef<Promise<void> | null>(null)
  const wasActiveRef = useRef(isActive)

  const { mutateAsync: updateTemplate } = useUpdateTemplateElement({
    documentPageId: documentPage?.id as string,
  })

  const stopTimers = useCallback(() => {
    if (delayRef.current !== null) {
      window.clearTimeout(delayRef.current)
      delayRef.current = null
    }
    if (intervalRef.current !== null) {
      window.clearInterval(intervalRef.current)
      intervalRef.current = null
    }
  }, [])

  const cancelCommitTimeout = useCallback(() => {
    if (commitTimeoutRef.current !== null) {
      window.clearTimeout(commitTimeoutRef.current)
      commitTimeoutRef.current = null
    }
  }, [])

  const resetPreview = useCallback(() => {
    const hadPreview = !isZeroPosition(preview)
    const hadPendingDelta = !isZeroPosition(pendingDeltaRef.current)

    stopTimers()
    cancelCommitTimeout()
    pendingDeltaRef.current = { x: 0, y: 0 }
    heldRef.current.clear()

    if (hadPreview) {
      setPreview(ZERO_POSITION)
    }

    if (hadPreview || hadPendingDelta) {
      onPreviewChange?.(ZERO_POSITION)
    }
  }, [preview, stopTimers, cancelCommitTimeout, onPreviewChange])

  const applyStep = useCallback(
    (code: string) => {
      if (!isActive) return
      const amount = shiftRef.current ? fineStep : step

      let dx = 0,
        dy = 0
      switch (code) {
        case 'ArrowLeft':
          dx = -amount
          break
        case 'ArrowRight':
          dx = amount
          break
        case 'ArrowUp':
          dy = -amount
          break
        case 'ArrowDown':
          dy = amount
          break
        default:
          return
      }

      setPreview((p) => {
        const next = { x: p.x + dx, y: p.y + dy }
        pendingDeltaRef.current = {
          x: pendingDeltaRef.current.x + dx,
          y: pendingDeltaRef.current.y + dy,
        }
        onPreviewChange?.(next)
        return next
      })
    },
    [fineStep, isActive, onPreviewChange, step],
  )

  const startRepeat = useCallback(() => {
    stopTimers()
    delayRef.current = window.setTimeout(() => {
      delayRef.current = null
      intervalRef.current = window.setInterval(() => {
        heldRef.current.forEach((code) => applyStep(code))
      }, repeatIntervalMs)
    }, repeatDelayMs) as unknown as number
  }, [applyStep, repeatDelayMs, repeatIntervalMs, stopTimers, isActive])

  const savePosition = useCallback(
    async (delta: Position) => {
      if (!template) return

      await updateTemplate(
        getTemplateAttributeMoveUpdate({
          delta: {
            x: delta.x / zoomPercentage,
            y: delta.y / zoomPercentage,
          },
          documentPage,
          template,
        }),
      )
    },
    [documentPage, template, updateTemplate, zoomPercentage],
  )

  const commit = useCallback(async () => {
    stopTimers()
    cancelCommitTimeout()

    const delta = {
      x: pendingDeltaRef.current.x,
      y: pendingDeltaRef.current.y,
    }

    if (delta.x === 0 && delta.y === 0) {
      resetPreview()
      return
    }

    const run = async () => {
      onCommit?.(delta)
      await savePosition(delta)
    }

    if (inFlightCommitRef.current) {
      inFlightCommitRef.current = inFlightCommitRef.current.then(run)
    } else {
      inFlightCommitRef.current = run()
    }

    await inFlightCommitRef.current
    inFlightCommitRef.current = null

    pendingDeltaRef.current = { x: 0, y: 0 }
    resetPreview()
  }, [onCommit, resetPreview, savePosition, stopTimers, cancelCommitTimeout])

  const scheduleCommit = useCallback(() => {
    cancelCommitTimeout()
    commitTimeoutRef.current = window.setTimeout(() => {
      commitTimeoutRef.current = null
      void (async () => {
        await commit()
      })()
    }, commitDebounceMs) as unknown as number
  }, [commit, commitDebounceMs, cancelCommitTimeout])

  const isEditableTarget = useCallback((target: EventTarget | null) => {
    if (!(target instanceof HTMLElement)) return false

    return (
      Boolean(target.closest('.ProseMirror')) ||
      Boolean(target.closest('[role="dialog"]')) ||
      Boolean(target.closest('[data-radix-popper-content-wrapper]')) ||
      Boolean(target.isContentEditable) ||
      target.tagName === 'INPUT' ||
      target.tagName === 'TEXTAREA' ||
      (target.tagName === 'DIV' && target.getAttribute('role') === 'textbox')
    )
  }, [])

  const onKeyDown = useCallback(
    (e: KeyboardNudgeEvent) => {
      if (!isActive) return
      if (isEditableTarget(e.target ?? null)) return

      const code = e.code
      if (!code.startsWith('Arrow')) return

      if ((e as unknown as KeyboardEvent).repeat) {
        if (preventDefault) e.preventDefault()
        e.stopPropagation()
        return
      }

      if (preventDefault) e.preventDefault()
      cancelCommitTimeout()
      onNudgeStart?.(showGridOnNudge)
      e.stopPropagation?.()

      shiftRef.current = e.shiftKey

      const firstArrow = heldRef.current.size === 0
      heldRef.current.add(code)

      applyStep(code)

      if (firstArrow) startRepeat()
    },
    [
      applyStep,
      isActive,
      isEditableTarget,
      preventDefault,
      startRepeat,
      cancelCommitTimeout,
      showGridOnNudge,
      onNudgeStart,
    ],
  )

  const onKeyUp = useCallback(
    (e: KeyboardNudgeEvent) => {
      if (!isActive) return
      if (isEditableTarget(e.target ?? null)) return

      const code = e.code

      if (code.startsWith('Arrow')) {
        if (preventDefault) e.preventDefault()
        e.stopPropagation?.()
        if (heldRef.current.size === 1) {
          onNudgeEnd?.(showGridOnNudge)
        }

        heldRef.current.delete(code)

        if (heldRef.current.size === 0) {
          scheduleCommit()
        }
      } else if (code === 'Escape') {
        if (preventDefault) e.preventDefault()
        e.stopPropagation?.()
        onNudgeEnd?.(showGridOnNudge)
        resetPreview()
      }
    },
    [
      isActive,
      isEditableTarget,
      preventDefault,
      resetPreview,
      onNudgeEnd,
      showGridOnNudge,
      scheduleCommit,
    ],
  )

  useEffect(() => {
    const wasActive = wasActiveRef.current
    wasActiveRef.current = isActive

    if (wasActive && !isActive) {
      pendingDeltaRef.current = { x: 0, y: 0 }
      resetPreview()
      onNudgeEnd?.(showGridOnNudge)
    }
  }, [isActive, resetPreview, onNudgeEnd, showGridOnNudge])

  useEffect(
    () => () => {
      stopTimers()
      cancelCommitTimeout()
    },
    [stopTimers, cancelCommitTimeout],
  )

  useEffect(() => {
    if (!bindToWindow || !isActive) return

    window.addEventListener('keydown', onKeyDown)
    window.addEventListener('keyup', onKeyUp)

    return () => {
      window.removeEventListener('keydown', onKeyDown)
      window.removeEventListener('keyup', onKeyUp)
    }
  }, [bindToWindow, isActive, onKeyDown, onKeyUp])

  return {
    bind: isActive
      ? {
          onKeyDown: onKeyDown as (e: React.KeyboardEvent) => void,
          onKeyUp: onKeyUp as (e: React.KeyboardEvent) => void,
        }
      : {},
    previewDelta: preview,
    resetPreview,
  }
}
